<template>
    <ies-form-field :field="field">
        <input type="hidden" :name="field.fieldName" :value="field.value">

        <div id="months_of_year">
            <div class="months-of-year-row months-of-year-select-all">
                <v-switch hide-details label="All" color="lime" v-model="selectAll"></v-switch>
            </div>
            <div class="months-of-year-row" v-for="key in monthKeys" :key="key">
                <label :for="key">{{ monthLabel(key) }}:</label>
                <input :name="key" type="checkbox" v-model="months[key]">
            </div>
        </div>
    </ies-form-field>
</template>

<script>
    /**
     * Allows the user to select the months of the year for which the given
     * field represents the best time to visit, mirroring DayOfWeek.vue's
     * bitwise view-model pattern but for 12 months instead of 7 days.
     *
     * The database stores the value as an integer with each month
     * represented by a bit (January = 1 through December = 2048), but the
     * user interacts with 12 checkboxes plus a "select all" toggle.
     */
    export default {
        name: 'ies-monthsOfYear',
        props: ['field'],
        components: {
            'ies-form-field': require('./FormField.vue').default,
        },
        data() {
            return {
                /** @var array monthKeys - ordered list of the view model's month property names */
                monthKeys: ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'],
                /** @var object months - A view model representing the months of the year and a boolean flag for each */
                months: {
                    january: false, february: false, march: false, april: false, may: false, june: false,
                    july: false, august: false, september: false, october: false, november: false, december: false,
                },
            }
        },
        computed: {
            selectAll: {
                get: function() {
                    return this.monthKeys.every(function(key) {
                        return this.months[key];
                    }.bind(this));
                },
                set: function(value) {
                    this.monthKeys.forEach(function(key) {
                        this.months[key] = value;
                    }.bind(this));
                },
            },
        },
        methods: {
            /**
             * Derives the 3-letter month abbreviation (e.g. "Jan") to
             * display next to each checkbox from its view model key
             */
            monthLabel: function(key) {
                return key.charAt(0).toUpperCase() + key.slice(1, 3);
            },

            /**
             * Update the view model so it reflects the underlying data
             * model for the field which will actually be saved to the
             * database later
             */
            updateViewFromModel: function() {
                this.months.january   = !!(this.field.value & 1);
                this.months.february  = !!(this.field.value & 2);
                this.months.march     = !!(this.field.value & 4);
                this.months.april     = !!(this.field.value & 8);
                this.months.may       = !!(this.field.value & 16);
                this.months.june      = !!(this.field.value & 32);
                this.months.july      = !!(this.field.value & 64);
                this.months.august    = !!(this.field.value & 128);
                this.months.september = !!(this.field.value & 256);
                this.months.october   = !!(this.field.value & 512);
                this.months.november  = !!(this.field.value & 1024);
                this.months.december  = !!(this.field.value & 2048);
            },

            /**
             * Update the data model for the field to match the data
             * represented in the view model which powers the user
             * interface so that it matches what the user has entered
             */
            updateModelFromView: function() {
                this.field.value = (this.months.january)   ? this.field.value |= 1    : this.field.value & ~1;
                this.field.value = (this.months.february)  ? this.field.value |= 2    : this.field.value & ~2;
                this.field.value = (this.months.march)     ? this.field.value |= 4    : this.field.value & ~4;
                this.field.value = (this.months.april)     ? this.field.value |= 8    : this.field.value & ~8;
                this.field.value = (this.months.may)       ? this.field.value |= 16   : this.field.value & ~16;
                this.field.value = (this.months.june)      ? this.field.value |= 32   : this.field.value & ~32;
                this.field.value = (this.months.july)      ? this.field.value |= 64   : this.field.value & ~64;
                this.field.value = (this.months.august)    ? this.field.value |= 128  : this.field.value & ~128;
                this.field.value = (this.months.september) ? this.field.value |= 256  : this.field.value & ~256;
                this.field.value = (this.months.october)   ? this.field.value |= 512  : this.field.value & ~512;
                this.field.value = (this.months.november)  ? this.field.value |= 1024 : this.field.value & ~1024;
                this.field.value = (this.months.december)  ? this.field.value |= 2048 : this.field.value & ~2048;
            }
        },

        created: function() {
            this.updateViewFromModel();
        },

        /**
         * In the event that the user changes the view model's data then
         * we need to trigger a corresponding change to the data model too
         */
        watch: {
            'months.january': function() {
                this.updateModelFromView();
            },
            'months.february': function() {
                this.updateModelFromView();
            },
            'months.march': function() {
                this.updateModelFromView();
            },
            'months.april': function() {
                this.updateModelFromView();
            },
            'months.may': function() {
                this.updateModelFromView();
            },
            'months.june': function() {
                this.updateModelFromView();
            },
            'months.july': function() {
                this.updateModelFromView();
            },
            'months.august': function() {
                this.updateModelFromView();
            },
            'months.september': function() {
                this.updateModelFromView();
            },
            'months.october': function() {
                this.updateModelFromView();
            },
            'months.november': function() {
                this.updateModelFromView();
            },
            'months.december': function() {
                this.updateModelFromView();
            }
        }
    }
</script>

<style>
#months_of_year .months-of-year-row {
    display: block;
}

#months_of_year label {
    display: inline-block;
    width: 3em;
    font-weight: normal;
}

#months_of_year .months-of-year-select-all {
    margin-bottom: 0.5em;
}
</style>
